import { NotificationItem } from "@/src/models/notification.model";
import {
  useGetNotifications,
  useMarkNotificationAsRead,
} from "@/src/services/api/notification/notification.hooks";
import {
  collapseDuplicateJoinRequests,
  isRenderableNotification,
} from "@/src/utils/notification.utils";
import { useTheme } from "@/src/styles/theme-provider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, Text, useWindowDimensions, View } from "react-native";
import { NotificationRow } from "../../notification/notification-row";
import NotificationSkeleton from "../../notification/notification-skeleton";

const getDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

type NotificationGroup = { label: string; items: NotificationItem[] };

const groupByDate = (
  notifications: NotificationItem[],
): NotificationGroup[] => {
  const groups: NotificationGroup[] = [];
  let currentLabel = "";
  let currentItems: NotificationItem[] = [];
  for (const item of notifications) {
    const label = getDateLabel(item.createdTime);
    if (label !== currentLabel) {
      if (currentItems.length > 0)
        groups.push({ label: currentLabel, items: currentItems });
      currentLabel = label;
      currentItems = [item];
    } else {
      currentItems.push(item);
    }
  }
  if (currentItems.length > 0)
    groups.push({ label: currentLabel, items: currentItems });
  return groups;
};

const TIMELINE_DOT_COLORS = [
  "--color-crimson-default",
  "--color-primary-default",
  "--color-jade-default",
  "--color-warning-default",
  "--color-info-default",
] as const;

type NotificationsProps = Readonly<{
  /**
   * Drives the server-side `IsUnreadOnly` filter. This used to be hardcoded
   * `true`, so the All tab showed exactly the same rows as Unread and a
   * notification vanished from the UI the moment it was marked read — the
   * history was never actually reachable. The screen passes the tab through;
   * it was being dropped by a `ComponentType` cast that declared a prop this
   * component did not accept.
   */
  isUnreadOnly?: boolean;
}>;

const Notifications = ({ isUnreadOnly = false }: NotificationsProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const [page, setPage] = useState(0);
  const { data, isPending } = useGetNotifications({
    Page: page,
    PageSize: 10,
    IsUnreadOnly: isUnreadOnly,
    SortIsDescending: false,
    SortProperty: "createdTime",
  });
  const [allNotifications, setAllNotifications] = useState<NotificationItem[]>(
    [],
  );

  // Switching tabs changes the query. Without resetting, page stays where it was
  // and the accumulator below appends the new tab's first page onto the old
  // tab's rows.
  useEffect(() => {
    setPage(0);
    setAllNotifications([]);
  }, [isUnreadOnly]);

  useEffect(() => {
    if (data?.notifications) {
      setAllNotifications((prev) =>
        page === 0 ? data.notifications : [...prev, ...data.notifications],
      );

      // Loud rather than silent: these rows used to render as empty cards with
      // no console trace of why. Logged per fetch, not per render.
      const dropped = (data.notifications as NotificationItem[]).filter(
        (n) => !isRenderableNotification(n),
      );
      if (dropped.length) {
        console.warn(
          `Notifications: hiding ${dropped.length} record(s) with no displayable message`,
          dropped.map((n) => ({ id: n.id, responseKey: n.payload?.responseKey })),
        );
      }
    }
  }, [data]);

  // The inbox is shared with the platform: Blocks' own records (environment
  // migrations and the like) arrive here carrying {title, description} instead of
  // {type, message}, so they render as blank cards. They cannot be deleted — the
  // platform exposes no per-user delete — so hiding them at render is the only
  // remedy. `allNotifications` stays raw so the pagination check below still
  // counts what the server actually returned.
  //
  // Repeated join requests are folded to the newest row per requester+group in
  // the same pass, so an admin sees one "wants to join" row rather than one per
  // request/cancel cycle.
  const renderable = useMemo(
    () =>
      collapseDuplicateJoinRequests(
        allNotifications.filter(isRenderableNotification),
      ),
    [allNotifications],
  );

  // Superseded join requests are also marked read, so they stop counting
  // towards the nav badge and stop coming back on the Unread tab — the closest
  // thing to deleting them that the platform allows. Each id is handled once;
  // marking read invalidates this query, and without the guard the refetch
  // would ask again.
  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const supersededHandled = useRef<Set<string>>(new Set());
  useEffect(() => {
    const kept = new Set(renderable.map((item) => item.id));
    for (const item of allNotifications) {
      if (kept.has(item.id) || item.isRead) continue;
      if (!isRenderableNotification(item)) continue;
      if (supersededHandled.current.has(item.id)) continue;
      supersededHandled.current.add(item.id);
      markAsRead(item.id);
    }
  }, [allNotifications, renderable, markAsRead]);

  // A whole page can be platform noise, and the empty state below unmounts the
  // ScrollView — so onScroll could never advance past such a page and the list
  // would strand on "no notifications" with more records available. Bounded by
  // the server's own total, so it cannot run past the end.
  useEffect(() => {
    const fetched = data?.notifications?.length ?? 0;
    const total = data?.totalNotificationsCount ?? 0;
    if (fetched === 0 || renderable.length > 0) return;
    if (allNotifications.length >= total) return;
    setPage((prev) => prev + 1);
  }, [data, renderable.length, allNotifications.length]);

  const groups = groupByDate(renderable);

  if (isPending && allNotifications.length === 0) {
    return <NotificationSkeleton count={6} />;
  }

  if (groups.length === 0) {
    return (
      <View className="flex-1 items-center justify-center py-20">
        <Text className="text-sm text-ash-1">{t("LABEL_NO_NOTIFCATIONS")}</Text>
      </View>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingVertical: 16,
        paddingRight: isMobile ? 0 : 16,
      }}
      onScroll={({ nativeEvent }) => {
        const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
        if (
          layoutMeasurement.height + contentOffset.y >=
          contentSize.height - 40
        ) {
          if (allNotifications.length >= 10) setPage((prev) => prev + 1);
        }
      }}
      scrollEventThrottle={400}
    >
      {groups.map((group, groupIndex) => {
        const dotColorKey =
          TIMELINE_DOT_COLORS[groupIndex % TIMELINE_DOT_COLORS.length];
        const dotColor = themeVars[dotColorKey] as string;

        if (isMobile) {
          return (
            <View key={group.label} className="mb-3">
              {/* Section label */}
              <Text className="text-xs font-semibold uppercase tracking-wide text-ash-1 mb-1 px-3">
                {group.label}
              </Text>

              {/* Rows */}
              <View className="gap-1">
                {group.items.map((item) => (
                  <NotificationRow key={item.id} item={item} />
                ))}
              </View>
            </View>
          );
        }

        return (
          <View key={group.label} className="flex-row">
            {/* ── LEFT timeline column (web only) ── */}
            <View className="w-20 items-center">
              <View className="h-9 justify-center items-center">
                <Text
                  className="text-xs text-ash-1 font-medium text-center w-16"
                  numberOfLines={2}
                >
                  {group.label}
                </Text>
              </View>
              <View
                className="w-3 h-3 rounded-full -mt-0.5"
                style={{
                  borderWidth: 2,
                  borderColor: dotColor,
                  backgroundColor: themeVars["--color-chalk-default"] as string,
                }}
              />
              <View
                className="flex-1 w-0.5 mt-1 min-h-5"
                style={{
                  backgroundColor: themeVars["--color-ash-3"] as string,
                }}
              />
            </View>

            {/* ── RIGHT cards column ── */}
            <View className="flex-1">
              <View className="h-9" />
              {group.items.map((item) => (
                <NotificationRow key={item.id} item={item} />
              ))}
              <View className="h-4" />
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
};

export default Notifications;
